<template>
  <div class="text-subtitle2 text-bold">フォーム設計</div>
  <div class="text-subtitle2 q-mt-sm">{{ formDescription }}</div>
  <div v-if="formType === 'chat'">
    <FormField v-model="value" :isDeleteFirst="isDeleteFirst" ref="formField" />
  </div>
  <div v-else>
    <div class="text-subtitle2 text-bold q-mt-sm">フォームサイズ</div>
    <q-select
      outlined
      dense
      v-model="currentFormSize"
      :options="options"
      emit-value
      map-options
      :rules="[(val) => (val && val.length > 0) || requiredRule.formName]"
    />
    <div>
      <q-item class="q-pa-none">
        <q-item-section style="min-width: 100px; max-width: 100px">
          <q-avatar rounded size="100px" v-if="currentFormImageHeaderUrl">
            <img :src="currentFormImageHeaderUrl" class="cursor-pointer" />
          </q-avatar>
          <q-avatar class="dotted-border radius-10 default-img" size="100px" icon="image" text-color="black" v-else />
        </q-item-section>
        <q-item-section side top>
          <q-btn
            size="sm"
            no-caps
            round
            outline
            icon="delete"
            color="red"
            @click="onRemoveImage()"
            v-if="currentFormImageHeaderUrl"
          />
        </q-item-section>
        <q-item-section style="min-width: 280px">
          <div class="q-gutter-sm q-ml-md">
            <ul>
              <li>推奨サイズ: 1:1 or フリーサイズ（自動調整）</li>
              <li>画像形式:jpg / jpeg / png / gif</li>
              <li>画像サイズ:10MB</li>
            </ul>
          </div>
        </q-item-section>
        <q-item-section style="min-width: 200px">
          <div class="q-gutter-sm">
            <q-file
              :label="$t('label.common.messages.add_image')"
              outlined
              dense
              accept=".jpg, image/*"
              v-model="file"
              bg-color="btn-upload"
              label-color="btn-upload"
              class="q-file-btn"
            >
              <template v-slot:prepend>
                <q-icon name="attach_file" size="xs" color="white" style="font-weight: bold" />
              </template>
            </q-file>
            <MediaUploader :is-crop-image="false" @on-select-media="onSelectMedia"></MediaUploader>
          </div>
        </q-item-section>
      </q-item>
    </div>
    <FormLIFFField v-model="value" :isDeleteFirst="isDeleteFirst" ref="formLiFFField" />
  </div>

  <UploadingProgress :files="cropFiles" />
  <CropperImageSelector
    v-if="cropperImageModalVisible"
    :modalVisible="cropperImageModalVisible"
    :file="file"
    @update:closeModal="onCloseModal"
    @update:onCropImage="onCropImage"
    :aspectRatio="1"
  />
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component'
import FormField from '@/components/form/FormField.vue'
import { Prop, Watch } from 'vue-property-decorator'
import { maska } from 'maska'
import { IFormDetail, IFormLIFFDetail, IFormLIFFImageDetail, IMedia } from '@/utils/types'
import FormLIFFField from '@/components/form/FormLIFFField.vue'
import { ASSETS_FORM_TYPE } from '@/utils/constants'
import UploadingProgress from '@/components/common/ui/UploadingProgress.vue'
import MediaUploader from '@/components/media-manager/MediaUploader.vue'
import CropperImageSelector from '@/components/tappable-area/selectors/CropperImageSelector.vue'
import UploadApi from '@/api/upload'

@Options({
  components: { FormLIFFField, FormField, MediaUploader, CropperImageSelector, UploadingProgress },
  directives: { maska },
  emits: ['update:modelValue', 'update:formSize', 'update:formImageHeaderUrl'],
})
export default class FormDetail extends Vue {
  @Prop({ default: [] })
  modelValue!: IFormDetail[] | IFormLIFFDetail[]

  @Prop()
  isDeleteFirst!: boolean

  @Prop()
  formType!: string

  @Prop()
  formSize!: string

  @Prop()
  formImageHeaderUrl!: string

  file: File | null = null
  // imageUrl = ''
  errorImageMessage = ''
  cropperImageModalVisible = false
  cropFiles: File[] = []

  get value() {
    return this.modelValue
  }

  set value(value: IFormDetail[] | IFormLIFFDetail[]) {
    this.$emit('update:modelValue', value)
  }

  get currentFormSize() {
    return this.formSize
  }

  set currentFormSize(value: string) {
    this.$emit('update:formSize', value)
  }

  get currentFormImageHeaderUrl() {
    return this.formImageHeaderUrl
  }

  set currentFormImageHeaderUrl(value: string) {
    this.$emit('update:formImageHeaderUrl', value)
  }

  get options() {
    return [
      {
        value: 'compact',
        label: this.$t('label.common.form_liff.size_compact'),
      },
      {
        value: 'tall',
        label: this.$t('label.common.form_liff.size_tall'),
      },
      {
        value: 'full',
        label: this.$t('label.common.form_liff.size_full'),
      },
    ]
  }

  get formDescription() {
    if (this.formType === ASSETS_FORM_TYPE.CHAT) {
      return 'チャット形式で一問一答で回答してもらう内容を設定してください。'
    }
    return 'フォームの表示サイズを選択し、設問を設計してください。'
  }

  get requiredRule() {
    return {
      formName: this.$t('validate.this_field_is_required', {
        placeholder: 'フォームサイズ',
      }),
    }
  }

  get headerImgUrl() {
    let url = ''
    const headerField = (this.value as IFormLIFFDetail[]).find((f) => f.option_type === 'image')
    if (headerField) {
      const item = headerField as IFormLIFFImageDetail
      url = item.img_url || ''
    }
    return url
  }

  onRemoveImage() {
    this.currentFormImageHeaderUrl = ''
  }

  async validateImageWidth(file: File) {
    return new Promise<boolean>((resolve) => {
      // Check file size (10MB limit)
      if (file.size >= 10 * 1024 * 1024) {
        this.errorImageMessage = this.$t('validate.image_size_too_large', {
          placeholder: file.name,
        })
        resolve(false)
        return
      }

      resolve(true)
    })
  }

  async submitFile(file: File) {
    try {
      const uploaded = await UploadApi.uploadFile(file)
      if (uploaded && this.value.length < 10) {
        this.currentFormImageHeaderUrl = uploaded.file_url_org ?? ''
      }
    } catch (error) {
      console.log(error)
    }
  }

  @Watch('file')
  async handleUpload() {
    if (!this.file) {
      return false
    }

    try {
      if (!(await this.validateImageWidth(this.file))) {
        // Show error message for file size validation
        this.$q.notify({
          type: 'negative',
          message: this.errorImageMessage,
          position: 'top',
        })
        this.onCloseModal()
      } else {
        this.cropFiles.push(this.file)
        await this.submitFile(this.file)
        this.file = null
        this.cropFiles = []
      }
    } catch (error) {
      console.log(error)
    }
  }

  onCloseModal() {
    this.cropperImageModalVisible = false
    this.errorImageMessage = ''
    this.file = null
    this.cropFiles = []
  }

  async onCropImage(file) {
    this.cropperImageModalVisible = false
    this.cropFiles.push(file)
    await this.submitFile(file)
    this.onCloseModal()
  }

  async onSelectMedia(file: IMedia) {
    if (file) {
      this.currentFormImageHeaderUrl = file.url || ''
    }
  }
}
</script>

<style scoped lang="scss">
</style>
